import type { Point } from "../canvas/canvasTypes";
import {
  hitTestSelectionTransform,
  normalizeSelectionRect,
  rectToSelectionTransform,
  selectionTransformToIntegerRect,
} from "./selectionGeometry";
import {
  SelectionInteractionController,
  type CompletedRasterSelectionTransform,
} from "./SelectionInteractionController";
import type {
  IntegerRect,
  RasterSelection,
  RasterSelectionHandle,
  RasterSelectionOverlayState,
  RasterSelectionTransform,
  RasterSelectionUiState,
} from "./selectionTypes";
import { EMPTY_RASTER_SELECTION_UI_STATE } from "./selectionTypes";

interface ActiveMarquee {
  readonly layerId: string;
  readonly start: Point;
  current: Point;
  readonly clipRect: IntegerRect;
  readonly panelId?: string;
}

export type CompletedRasterSelectionInteraction =
  | {
      readonly kind: "marquee";
      readonly layerId: string;
      readonly rect: IntegerRect | null;
      readonly clipRect: IntegerRect;
      readonly panelId?: string;
    }
  | ({ readonly kind: "transform" } & CompletedRasterSelectionTransform);

function cloneSelection(selection: Readonly<RasterSelection>): RasterSelection {
  return {
    layerId: selection.layerId,
    sourceRect: { ...selection.sourceRect },
    clipRect: { ...selection.clipRect },
    panelId: selection.panelId,
  };
}

export class RasterSelectionController {
  private selection: RasterSelection | null = null;
  private marquee: ActiveMarquee | null = null;
  private readonly transformInteraction: SelectionInteractionController;

  public constructor(transformThresholdCssPixels?: number) {
    this.transformInteraction = new SelectionInteractionController(transformThresholdCssPixels);
  }

  public setSelection(selection: Readonly<RasterSelection>): void {
    this.cancelInteraction();
    this.selection = cloneSelection(selection);
  }

  public commitTransform(transform: Readonly<RasterSelectionTransform>): void {
    if (!this.selection) return;
    this.selection = {
      ...this.selection,
      sourceRect: selectionTransformToIntegerRect(transform, this.selection.clipRect),
    };
  }

  public clear(): boolean {
    const changed = this.selection !== null || this.isActive();
    this.cancelInteraction();
    this.selection = null;
    return changed;
  }

  public getSelection(): RasterSelection | null {
    return this.selection ? cloneSelection(this.selection) : null;
  }

  public getTransform(): RasterSelectionTransform | null {
    if (!this.selection) return null;
    return (
      this.transformInteraction.getPreview() ?? rectToSelectionTransform(this.selection.sourceRect)
    );
  }

  public hitTest(point: Readonly<Point>, zoom: number): RasterSelectionHandle | null {
    const transform = this.getTransform();
    return transform && this.selection
      ? hitTestSelectionTransform(
          point,
          transform,
          zoom,
          this.selection.clipRect,
          this.transformInteraction.getRotationHandleSide(),
        )
      : null;
  }

  public beginMarquee(
    layerId: string,
    point: Readonly<Point>,
    clipRect: Readonly<IntegerRect>,
    panelId?: string,
  ): void {
    this.cancelInteraction();
    this.selection = null;
    this.marquee = {
      layerId,
      start: { ...point },
      current: { ...point },
      clipRect: { ...clipRect },
      panelId,
    };
  }

  public beginTransform(
    handle: RasterSelectionHandle,
    point: Readonly<Point>,
    zoom: number,
  ): boolean {
    if (!this.selection || this.marquee) return false;
    this.transformInteraction.begin(
      rectToSelectionTransform(this.selection.sourceRect),
      this.selection.clipRect,
      handle,
      point,
      zoom,
    );
    return true;
  }

  public update(point: Readonly<Point>): boolean {
    if (this.marquee) {
      this.marquee.current = { ...point };
      return true;
    }
    return Boolean(this.transformInteraction.update(point));
  }

  public finishInteraction(): CompletedRasterSelectionInteraction | null {
    if (this.marquee) {
      const marquee = this.marquee;
      this.marquee = null;
      return {
        kind: "marquee",
        layerId: marquee.layerId,
        rect: normalizeSelectionRect(marquee.start, marquee.current, marquee.clipRect),
        clipRect: { ...marquee.clipRect },
        panelId: marquee.panelId,
      };
    }
    const completed = this.transformInteraction.finish();
    return completed ? { kind: "transform", ...completed } : null;
  }

  public cancelInteraction(): boolean {
    const wasActive = this.isActive();
    this.marquee = null;
    this.transformInteraction.cancel();
    return wasActive;
  }

  public isActive(): boolean {
    return this.marquee !== null || this.transformInteraction.isActive();
  }

  public isTransformActive(): boolean {
    return this.transformInteraction.isActive();
  }

  public getOverlayState(): RasterSelectionOverlayState {
    const marquee = this.marquee
      ? normalizeSelectionRect(this.marquee.start, this.marquee.current, this.marquee.clipRect)
      : null;
    return {
      selection: this.selection ? cloneSelection(this.selection) : null,
      marquee,
      transform: this.getTransform(),
      transformActive: this.transformInteraction.isActive(),
      rotationHandleSide: this.transformInteraction.getRotationHandleSide(),
    };
  }

  public getUiState(): RasterSelectionUiState {
    if (!this.selection) return { ...EMPTY_RASTER_SELECTION_UI_STATE };
    return {
      hasSelection: true,
      isTransforming: this.transformInteraction.isActive(),
      layerId: this.selection.layerId,
      bounds: { ...this.selection.sourceRect },
    };
  }
}
